<template>
  <div>
    <Head :title="__(title)" />
    <div class="bg-white rounded-md shadow overflow-hidden max-w-full">
      <form @submit.prevent="store">
        <div class="p-8 -mr-6 -mb-8 flex flex-wrap">
          <!-- Other input fields -->
          <text-input
            v-model="form.date"
            :error="form.errors.date"
            type="date"
            class="pr-6 pb-8 w-full lg:w-1/3"
            :label="__('Date')"
          />

          <select-input-filter
            placeholder="Select Customer"
            :onInput="filterOrganization"
            :items="organization"
            v-model="form.organization_id"
            :error="form.errors.organization_id"
            class="pr-6 pb-8 w-full lg:w-1/3"
            :label="__('Customer')"
            :required="true"
          />

          <select-input-filter
            placeholder="Select User"
            :onInput="filterCustomer"
            :items="customer"
            v-model="form.customer_id"
            :error="form.errors.customer_id"
            class="pr-6 pb-8 w-full lg:w-1/3"
            :label="__('User')"
            :required="true"
          />

          <select-input
            v-model="form.type_outgoing"
            :required="true"
            :error="form.errors.type_outgoing"
            class="pr-6 pb-8 w-full lg:w-1/3"
            :label="__('Type Outgoing')"
          >
            <option :value="null">{{ __('Select Type') }}</option>
            <option v-for="s in type_outgoings" :key="s.value" :value="s.value">{{ s.name }}</option>
          </select-input>

          <textarea-input
            v-model="form.description"
            :error="form.errors.description"
            :rows="8"
            class="pb-6 w-full lg:w-1/1"
            placeholder="description here.."
            :label="__('Note')"
          ></textarea-input>
          <!-- Detail Section -->
          <div class="pr-6 pb-8 w-full">
            <label class="form-label mb-6">{{ __('Details') }}:</label>

            <!-- Table for Details -->
            <div class="bg-white rounded-md shadow overflow-x-auto">
              <!-- Global error for details array -->
              <div
                v-if="form.errors.details"
                class="text-red-500 text-sm px-4 py-2"
              >{{ form.errors.details }}</div>

              <table class="min-w-full">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-4 py-2 text-left">{{ __('No') }}</th>
                    <th class="px-4 py-2 text-left">{{ __('Category') }}</th>
                    <th class="px-4 py-2 text-left">{{ __('Brand') }}</th>
                    <th class="px-4 py-2 text-left">{{ __('Type') }}</th>
                    <th class="px-4 py-2 text-left">{{ __('Serial Number') }}</th>
                    <th class="px-4 py-2 text-left">{{ __('User') }}</th>
                    <th class="px-4 py-2 text-left">{{ __('Qty') }}</th>
                    <th class="px-4 py-2 text-left">{{ __('Action') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(detail, index) in form.details" :key="index">
                    <td class="px-4 py-2">{{ index + 1 }}</td>
                    <td class="px-4 py-2">{{ detail.category }}</td>
                    <td class="px-4 py-2">{{ detail.brand }}</td>
                    <td class="px-4 py-2">{{ detail.type }}</td>
                    <td class="px-4 py-2">{{ detail.serial_number_assets }}</td>

                    <!-- User Input Field -->
                    <td class="px-4 py-2">
                      <select-input-filter
                        placeholder="Select User"
                        :onInput="filterCustomerTicket"
                        :items="customer_ticket"
                        v-model="detail.user_id"
                        :error="form.errors[`details.${index}.user_id`]"
                        class="w-full"
                      />
                      <span
                        v-if="form.errors[`details.${index}.user`]"
                        class="text-red-500 text-sm"
                      >{{ form.errors[`details.${index}.user`] }}</span>
                    </td>

                    <!-- Qty Input Field -->
                    <td class="px-4 py-2">
                      <text-input
                        v-model="detail.qty"
                        :error="form.errors[`details.${index}.qty`]"
                        type="number"
                        min="1"
                        class="w-full"
                      />
                      <span
                        v-if="form.errors[`details.${index}.qty`]"
                        class="text-red-500 text-sm"
                      >{{ form.errors[`details.${index}.qty`] }}</span>
                    </td>

                    <!-- Action (Remove Row) -->
                    <td class="px-4 py-2">
                      <button
                        type="button"
                        @click="removeDetail(index)"
                        class="text-red-500"
                      >{{ __('Remove') }}</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <button
              class="text-blue-500 mt-12"
              type="button"
              @click="openModal"
            >{{ __('Add another asset') }}</button>
          </div>
        </div>
        <div class="px-8 py-4 bg-gray-50 border-t border-gray-100 flex justify-end items-center">
          <loading-button
            :loading="form.processing"
            class="btn-indigo"
            type="submit"
          >{{ __('Create Outgoing Goods') }}</loading-button>
        </div>
      </form>

      <!-- Modal for Adding Asset -->
      <ModalAsset :showModal="showModal" @close="closeModal" @assetsSelected="addAssetDetails" />
    </div>
  </div>
</template>

  <script>
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import SelectInputFilter from '@/Shared/SelectInputFilter'
import ModalAsset from '@/Pages/ExitGoods/ModalAsset'
import axios from 'axios'
import TextareaInput from '@/Shared/TextareaInput'

export default {
  metaInfo: { title: 'Create a new outgoing goods' },
  components: {
    LoadingButton,
    SelectInput,
    TextInput,
    SelectInputFilter,
    ModalAsset,
    TextareaInput,
  },
  layout: Layout,
  props: {
    title: String,
    customer: Array,
    customer_ticket: Array,
    organization: Array,
    category: Array,
    brand: Array,
    type_outgoings: Array,
  },
  data() {
    return {
      form: this.$inertia.form({
        date: null,
        customer_id: '',
        organization_id: '',
        description: '',
        type_outgoing: '',
        details: [],
      }),
      showModal: false,
    }
  },
  methods: {
    openModal() {
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
    },
    addAssetDetails(selectedAssets) {
      selectedAssets.forEach((asset) => {
        this.form.details.push({
          asset_id: asset.id,
          category: asset.category,
          brand: asset.brand,
          type: asset.type,
          serial_number_assets: asset.serial_number,
          user_id: null,
          qty: 1,
        })
      })
      this.closeModal()
    },
    removeDetail(index) {
      this.form.details.splice(index, 1)
      this.customer_ticket.splice(index, 1) // Remove customer_ticket for the corresponding detail
    },
    store() {
      this.form.post(this.route('exit_goods.store'))
    },
    filterOrganization(e) {
      axios.get(this.route('filter.organizations', { search: e.target.value })).then((res) => {
        this.organization.splice(0, this.organization.length, ...res.data)
      })
    },
    filterCustomer(e) {
      axios.get(this.route('filter.outgoing_user', { search: e.target.value })).then((res) => {
        this.customer.splice(0, this.customer.length, ...res.data)
      })
    },
    filterCustomerTicket(e) {
      axios.get(this.route('filter.customer_ticket', { search: e.target.value })).then((res) => {
        this.customer_ticket.splice(0, this.customer_ticket.length, ...res.data)
      })
    },
  },
}
</script>
